import classNames from "classnames";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import {
	CSSProperties,
	KeyboardEvent,
	ReactElement,
	Ref,
	useContext,
	useMemo
} from "react";
import { DropOverContext } from "../DropOver/DropOver";
import { Highlight, HighlightedText } from "../HighlightedText/HighlightedText";
import {
	Icon,
	isSvgIcon,
	IconValue,
	unsetIconColor
} from "../Icon/Icon";
import styles from "./ActionListItem.module.less";

export type ActionListItemProps = {
	icon?: IconValue;
	text: string;
	title?: string;
	highlight?: Highlight | nil;
	suffix?: string | ReactElement;
	subMenuIcon?: "visible" | "hidden" | "collapsed";
	onClick?(): void;
	onKeyUp?: (args: KeyboardEvent<HTMLLIElement>) => void;
	onKeyDown?: (args: KeyboardEvent<HTMLLIElement>) => void;
	trackingId?: string;
	rightIcon?: IconValue;
	shouldNotAutoCloseContainingDropOver?: boolean;
	isDisabled?: boolean;
	bubbleClick?: boolean;
	tabIndex?: number;
	indentionLevel?: number;
	isActive?: boolean;
	role?: "option";
	ref?: Ref<HTMLLIElement>;
};

export const ActionListItem = (props: ActionListItemProps): ReactElement => {
	const {
		icon,
		text,
		title,
		highlight,
		suffix,
		subMenuIcon = "collapsed",
		onClick,
		onKeyUp,
		onKeyDown,
		trackingId,
		rightIcon,
		shouldNotAutoCloseContainingDropOver = false,
		isDisabled = false,
		bubbleClick,
		tabIndex,
		indentionLevel,
		isActive,
		role,
		ref
	} = props;

	const {
		onClose
	} = useContext(DropOverContext);

	const clickHandler = (args: React.MouseEvent): void => {
		if (isDisabled) {
			return;
		}

		if (bubbleClick === false) {
			args.preventDefault();
			args.stopPropagation();
		}

		if (!shouldNotAutoCloseContainingDropOver && onClose){
			onClose();
		}

		onClick?.();
	};

	const style: CSSProperties = useIndention(indentionLevel);

	return (
		<li
			{...setTrackingId(trackingId)}
			aria-selected={isActive}
			role={role}
			className={classNames(
				styles.root,
				{
					[styles.root_hoverable]: !isDisabled && isNotNil(onClick),
					[styles.root_disabled]: isDisabled,
					[styles.root_active]: isActive
				},
			)}
			style={style}
			onClick={clickHandler}
			onKeyUp={onKeyUp}
			onKeyDown={onKeyDown}
			title={title ?? text}
			ref={ref}
			tabIndex={tabIndex}
			aria-disabled={isDisabled}
		>
			<div className={styles.contentHost}>
				{icon && (
					<span
						className={classNames(
							styles.icon,
							{ [styles.icon_svg]: isSvgIcon(icon) }
						)}
					>
						<Icon value={icon} />
					</span>
				)}
				<span className={styles.text}>
					{highlight
						? <HighlightedText text={text} highlight={highlight}/>
						: text
					}
				</span>
				<div className={styles.rightAndSubMenuIcon}>
					{suffix && (
						<span>
							{suffix}
						</span>
					)}
					{rightIcon && (
						<span
							className={classNames(
								styles.icon,
								styles.icon_right,
								{ [styles.icon_svg]: isSvgIcon(rightIcon) }
							)}
						>
							<Icon
								value={isDisabled
									? unsetIconColor(rightIcon)
									: rightIcon
								}
							/>
						</span>
					)}

					<span
						className={classNames(
							styles.icon,
							styles.icon_inSubMenu,
							{
								[styles.icon_inSubMenu_hidden]: subMenuIcon === "hidden",
								[styles.icon_inSubMenu_collapsed]: subMenuIcon === "collapsed",
							}
						)}
					>
						<Icon value="chevron-right" />
					</span>
				</div>
			</div>
		</li>
	);
};
ActionListItem.displayName = "ActionListItem";

export function useIndention(level: number | undefined): CSSProperties {
	return useMemo(
		(): CSSProperties => {
			const result: CSSProperties = {};
			result["--action-list-item-indention-level"] = level ?? 0;
			return result;
		},
		[level]
	);
}
